import type { Member } from '@ebalance/proto/bffuser/v1/user_directory_model_pb'
import i18next from 'i18next'

/**
 * 自動の処理が記録する実行者の識別子 (確定者・完了者等に入る)。
 * backend `valueobject.AutomaticExecutionActorID`
 * (`go/internal/backend/domain/valueobject/automatic_execution_actor.go`) の写しで、
 * BFF は変換せずに通す。 表示名は spec `workflow/workflow-automation.md` 付録の
 * 「自動実行」(AC-WFA-003)。 旧機能の撤去 (#10220) まで残る `system-auto-submit` は含めない。
 */
export const AUTOMATIC_EXECUTION_ACTOR_ID = 'system-automatic-execution'

/**
 * member_id を member 一覧 (`useListMembers`) から表示名に解決するヘルパー
 * (workflow-substep-assignee design: completed_by / assignee_member_id を
 * 表示名から member_id へ変更したことに伴い、frontend 側で名前解決が必要になった)。
 *
 * アプリ横断 (`src/models/`) に置くのは WorkflowCaseDetail の complete/assignee 表示、
 * AdminWorkflowTemplates の担当者表示等、複数ページで同じ解決ロジックが必要なため
 * (page-local model の複製を避ける)。
 *
 * - memberId が自動実行の実行者 → 「自動実行」(member 一覧の取得状態に依らない。AC-WFA-003)
 * - memberId が undefined または空文字 → undefined (= 「未設定」を表す。呼び出し側で
 *   「-」等のプレースホルダに変換する)
 * - member 一覧に一致する memberId があれば displayName を返す
 * - 一致が無ければ undefined (= 呼び出し側でプレースホルダ表示)。**生の memberId は
 *   決して返さない**: memberId は eauth member ID (= auth0 subject `auth0|...`) で
 *   あり、そのまま返すと一覧読み込み中・退職済みメンバー等のケースで内部 ID が
 *   画面に露出するため (バグ報告: 広域マスタの最終更新列 / 監査情報に auth0|... が表示)
 */
export function resolveMemberDisplayName(
  members: Member[],
  memberId: string | undefined,
): string | undefined {
  if (memberId === undefined || memberId === '') return undefined
  if (memberId === AUTOMATIC_EXECUTION_ACTOR_ID) {
    return i18next.t('common:actor.automaticExecution')
  }
  const displayName = members.find((m) => m.memberId === memberId)?.displayName
  return displayName === undefined || displayName === ''
    ? undefined
    : displayName
}
